// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
// CP4.1: Vue 2 (new Vue) -> Vue 3 (createApp) under @vue/compat MODE 3. element-ui -> element-plus,
// bootstrap-vue -> bootstrap-vue-next, event bus -> mitt. Mirrors the oracle main.js; PA-specific bits
// (HttpAdapter CP2.3 adapter, the migration console-unblind, DD_RUM) are preserved.
import { createApp, configureCompat } from 'vue'
import App from './App.vue'
import HttpAdapter from './plugins/http'
import ElementPlus from 'element-plus'
import locale from 'element-plus/es/locale/lang/en'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import store from './store/store'
import router from './router'
import VueWorker from 'vue-worker'
import Mixpanel from './plugins/mixpanel'
import BadRequestInterceptor from './plugins/interceptors/bad_request'
import UnauthorizedRequestInterceptor from './plugins/interceptors/unauthorized_request'
// import InstanceSwitchGuard from './plugins/instance_switch_guard'
import PageVisibilityCheck from './plugins/page_visibility_check'
import SigmaPlugin from '@sigmacloud/sigma-client/dist/components/plugins/sigma'
// CP4.2: do NOT import from @sigmacloud/sigma-client/dist/components/plugins — that module's top level runs
// `Vue.use(BootstrapVue)` / `Vue.use(BootstrapVueIcons)` (Vue-2 bootstrap-vue, removed here) as an import side
// effect, which executes at boot. The frontend only needs its tiny warn-suppression helper, inlined below.
import { IS_DEV } from '@sigmacloud/sigma-client/dist/util/env'
import { createBootstrap } from 'bootstrap-vue-next'
import { BootstrapIconsPlugin } from 'bootstrap-icons-vue'
import { registerBootstrapVueNext } from './plugins/bootstrap-vue-next-components'
import emitter from './eventBus'
import 'element-plus/dist/index.css'
// bootstrap-vue-next.css only styles bootstrap-vue-next's own additions — it assumes Bootstrap core is
// present. We deliberately do NOT load full bootstrap.css (its reboot restyles *, body, a, button, input,
// etc. and would clobber element-plus). bootstrap-grid + bootstrap-utilities are reboot-free (class-only,
// self-contained --bs-* vars), so they safely restore the grid (b-container/row/col) and spacing/flex
// utilities the bootstrap-vue report screens rely on. Nav/navbar component styles aren't in these bundles;
// they're added, scoped, in main.css. Load before main.css so app rules still win.
import 'bootstrap/dist/css/bootstrap-grid.css'
import 'bootstrap/dist/css/bootstrap-utilities.css'
import './assets/styles/main.css'
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'

const app = createApp(App)

// MODE 3 with the Vue-2 opt-ins the app relies on; COMPONENT_V_MODEL:false so bootstrap-vue-next's native
// v-model is not rewritten to the Vue-2 value/input contract (the app's own fields keep their own bindings).
configureCompat({
  MODE: 3,
  INSTANCE_CHILDREN: true,
  WATCH_ARRAY: false,
  COMPONENT_V_MODEL: false,
  RENDER_FUNCTION: false,
  ATTR_ENUMERATED_COERCION: false
})

app.config.globalProperties.$emitter = emitter
app.use(ElementPlus, { locale })
app.use(HttpAdapter) // CP2.3: vue-resource-compatible adapter over axios (installed before the interceptors)
app.use(VueWorker)
const mixpanelToken = process.env.MIXPANEL_TOKEN || ''
app.use(Mixpanel, { token: mixpanelToken })
// CP (Vue 3): store modules / interceptors / helpers now import the `http` and `mixpanel` singletons directly
// from their plugins (@/plugins/http, @/plugins/mixpanel), so the former `Vue.http` / `Vue.mixpanel` compat-global
// bridge is no longer needed and has been removed.
app.use(BadRequestInterceptor)
app.use(UnauthorizedRequestInterceptor)
// CP (Vue 3): the auth verifier is a plain module now (start/stopAuthVerifier), imported directly by auth.js —
// it no longer needs a Vue plugin / app.use (the statics did not reach the `Vue` compat global; see auth_verifier.js).
// app.use(InstanceSwitchGuard)
app.use(PageVisibilityCheck)
app.use(SigmaPlugin)
app.use(createBootstrap())
app.use(BootstrapIconsPlugin)
registerBootstrapVueNext(app)

for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

// Migration CP0.4 (issue-review process): the console is UNBLINDED for the duration of the Vue 3
// migration so `npm run dev` surfaces Vue warnings + @vue/compat deprecations. Flip this back to re-enable
// warning suppression after cutover. (Keeps both imports referenced so lint stays green.)
const UNBLIND_CONSOLE_FOR_MIGRATION = true
// Inlined from sigma-client's enableIgnoreVueWarn (Vue-3 form: warnHandler is per-app config).
const enableIgnoreVueWarn = (...apps) => {
  const nothingHandler = () => {}
  apps.forEach((a) => { if (a && a.config) a.config.warnHandler = nothingHandler })
}
if (IS_DEV && !UNBLIND_CONSOLE_FOR_MIGRATION) {
  enableIgnoreVueWarn(app)
}

app.use(store)
app.use(router)
app.mount('#app')

if (process.env.DD_APPLICATION_ID && process.env.DD_CLIENT_TOKEN) {
  router.isReady().then(() => {
    window.DD_RUM.init({
      applicationId: process.env.DD_APPLICATION_ID,
      clientToken: process.env.DD_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'sigma-frontend',
      env: process.env.DD_ENV,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input'
    })
    window.DD_RUM.startSessionReplayRecording()
    const user = store.getters.getUser
    if (user && user.isLoggedIn) {
      const instanceUrl = sessionStorage.getItem('instanceUrl') || ''
      const instanceName = instanceUrl.replace(/^https?:\/\//i, '').replace(/\/.*$/, '').split('.')[0]
      window.DD_RUM.setUser({ id: user.userId, name: user.username, email: user.email, instance: instanceName })
    }
  })
}
